/**
 * Right panel form for the Details step.
 * Uses the new FormBuilder from @kula-ai/components with a config-driven schema.
 */
import { forwardRef, memo, useCallback, useRef, useState } from 'react';
import { FormBuilder, type FormBuilderHandle } from '@kula-ai/components';
import type { ScheduleMode, ApiReference } from '../../schedule.types';
import { useDetailsFormSchema } from './useDetailsFormSchema';
import { useDetailsFormDefaults } from './useDetailsFormDefaults';

interface DetailsFormProps {
  mode: ScheduleMode;
  apiReference: ApiReference;
  interviewPlan?: Record<string, unknown>;
  onFieldChange?: (
    _name: string,
    _value: unknown,
    _allValues: Record<string, unknown>
  ) => void;
}

export const DetailsForm = memo(
  forwardRef<FormBuilderHandle, DetailsFormProps>(
    ({ mode, apiReference, interviewPlan, onFieldChange }, ref) => {
      const internalRef = useRef<FormBuilderHandle>(null);

      const setRefs = useCallback(
        (node: FormBuilderHandle | null) => {
          internalRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref)
            (ref as React.MutableRefObject<FormBuilderHandle | null>).current =
              node;
        },
        [ref]
      );

      const defaultInterviewType =
        (interviewPlan?.kind as string) ??
        (interviewPlan?.interview_kind as string) ??
        'one_on_one';

      const [interviewType, setInterviewType] = useState(defaultInterviewType);
      // Track startDate and startTime for dynamic time option filtering
      const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
        null
      );
      const [selectedStartTime, setSelectedStartTime] = useState('');

      const schema = useDetailsFormSchema({
        mode,
        apiReference,
        meetingRoomsEnabled: Boolean(interviewPlan?.meeting_rooms_enabled),
        interviewType,
        selectedStartDate,
        selectedStartTime,
      });
      const defaultValues = useDetailsFormDefaults(
        interviewPlan,
        mode,
        apiReference
      );

      const handleFieldChange = useCallback(
        (name: string, value: unknown, allValues: Record<string, unknown>) => {
          if (name === 'interview_type') {
            const newType = value as string;
            setInterviewType(newType);

            const current = allValues.participant_ids;
            if (newType === 'one_on_one') {
              if (Array.isArray(current) && current.length > 0) {
                internalRef.current?.setValue('participant_ids', current[0]);
              }
            }
            if (newType === 'panel') {
              if (current && !Array.isArray(current)) {
                internalRef.current?.setValue('participant_ids', [current]);
              }
            }
          }

          // Track date/time for dynamic option filtering
          if (name === 'startDate') {
            setSelectedStartDate(value as Date | null);
          }
          if (name === 'startTime') {
            setSelectedStartTime((value as string) || '');
          }

          onFieldChange?.(name, value, allValues);
        },
        [onFieldChange]
      );

      return (
        <FormBuilder
          ref={setRefs}
          schema={schema as any}
          defaultValues={defaultValues}
          onFieldChange={handleFieldChange}
          columns={1}
          mode="onBlur"
        />
      );
    }
  )
);

DetailsForm.displayName = 'DetailsForm';
